// Shared audio context and cached audio element for instant low-latency playback
let sharedAudioCtx: AudioContext | null = null;
let cachedAudio: HTMLAudioElement | null = null;
let isUnlocked = false;

// Preload audio and register unlock listener on first user interaction
const unlockAudio = () => {
    if (isUnlocked) return;
    try {
        if (!cachedAudio && typeof Audio !== "undefined") {
            cachedAudio = new Audio("/sounds/notification.wav");
            cachedAudio.volume = 0.7;
            cachedAudio.load();
        }

        const AudioContextClass =
            window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass && !sharedAudioCtx) {
            sharedAudioCtx = new AudioContextClass();
            if (sharedAudioCtx.state === "suspended") {
                sharedAudioCtx.resume().catch(() => { });
            }
        } else if (sharedAudioCtx && sharedAudioCtx.state === "suspended") {
            sharedAudioCtx.resume().catch(() => { });
        }

        isUnlocked = true;
    } catch {
        // ignore
    }
};

// Auto-register user interaction listeners to satisfy browser autoplay policies
if (typeof window !== "undefined") {
    const events = ["click", "keydown", "touchstart", "pointerdown"];
    const onInteraction = () => {
        unlockAudio();
        events.forEach((ev) => window.removeEventListener(ev, onInteraction));
    };
    events.forEach((ev) =>
        window.addEventListener(ev, onInteraction, { passive: true })
    );
}

/**
 * Synthesizes a pleasant dual-tone chime using Web Audio API
 */
const playWebAudioChime = () => {
    try {
        const AudioContextClass =
            window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) return;

        if (!sharedAudioCtx) {
            sharedAudioCtx = new AudioContextClass();
        }

        const ctx = sharedAudioCtx;
        const playTones = () => {
            const now = ctx.currentTime;

            // Tone 1: 830.6Hz (G#5)
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.type = "sine";
            osc1.frequency.setValueAtTime(830.61, now);
            gain1.gain.setValueAtTime(0.35, now);
            gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
            osc1.connect(gain1);
            gain1.connect(ctx.destination);
            osc1.start(now);
            osc1.stop(now + 0.35);

            // Tone 2: 1046.5Hz (C6)
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.type = "sine";
            osc2.frequency.setValueAtTime(1046.5, now + 0.12);
            gain2.gain.setValueAtTime(0.4, now + 0.12);
            gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.start(now + 0.12);
            osc2.stop(now + 0.6);
        };

        if (ctx.state === "suspended") {
            ctx.resume().then(playTones).catch(() => { });
        } else {
            playTones();
        }
    } catch (e) {
        console.warn("[SFX] Web Audio chime failed:", e);
    }
};

// Cooldown to prevent duplicate sound playback within short bursts (e.g. concurrent FCM & poll)
let lastSoundPlayedAt = 0;
const SOUND_COOLDOWN_MS = 2000;

/**
 * Plays notification sound using HTMLAudioElement with Web Audio fallback
 */
export const playNotificationSound = (force = false) => {
    const now = Date.now();
    if (!force && now - lastSoundPlayedAt < SOUND_COOLDOWN_MS) {
        console.log("[SFX] Suppressed duplicate notification sound (cooldown active)");
        return;
    }
    lastSoundPlayedAt = now;

    try {
        if (!cachedAudio && typeof Audio !== "undefined") {
            cachedAudio = new Audio("/sounds/notification.wav");
            cachedAudio.volume = 0.7;
        }

        if (cachedAudio) {
            cachedAudio.currentTime = 0;
            const playPromise = cachedAudio.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        console.log("[SFX] Played notification chime via Audio element");
                    })
                    .catch((err) => {
                        console.warn("[SFX] Audio element blocked, attempting Web Audio API:", err);
                        playWebAudioChime();
                    });
                return;
            }
        }

        playWebAudioChime();
    } catch (err) {
        console.warn("[SFX] Notification sound error:", err);
        playWebAudioChime();
    }
};
